import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { PlayArrow as PlayArrowIcon } from "@mui/icons-material";
import { useAdminWorkflowAction } from "./useAdminWorkflowAction";

export interface AdminWorkflowActionTrackerProps {
  command: string;
  commandArgs: Record<string, any>;
  buttonLabel?: string;
  disabled?: boolean;
  compact?: boolean;
  pollInterval?: number;
  /** If set, clicking the button shows a confirmation dialog with this message before executing */
  confirmMessage?: string;
  /** Called when the workflow completes */
  onComplete?: () => void;
}

export function AdminWorkflowActionTracker({
  command,
  commandArgs,
  buttonLabel = "Run",
  disabled = false,
  compact = false,
  pollInterval,
  confirmMessage,
  onComplete,
}: AdminWorkflowActionTrackerProps) {
  const {
    execute,
    isStarting,
    isTracking,
    isComplete,
    progress,
    startError,
    reset,
  } = useAdminWorkflowAction({ command, pollInterval, onComplete });

  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleClick = () => {
    if (confirmMessage) {
      setConfirmOpen(true);
    } else {
      execute(commandArgs);
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    execute(commandArgs);
  };

  const confirmDialog = confirmMessage ? (
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>Confirm</DialogTitle>
      <DialogContent>
        <Typography>{confirmMessage}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" color="warning">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;

  // Idle state — show trigger button
  if (!isStarting && !isTracking && !isComplete && !startError) {
    return (
      <>
        <Button
          variant="outlined"
          size={compact ? "small" : "medium"}
          startIcon={<PlayArrowIcon />}
          onClick={handleClick}
          disabled={disabled}
        >
          {buttonLabel}
        </Button>
        {confirmDialog}
      </>
    );
  }

  // Starting state
  if (isStarting) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Starting workflow...
        </Typography>
      </Box>
    );
  }

  // Start error
  if (startError) {
    return (
      <Alert
        severity="error"
        sx={{ py: compact ? 0.5 : 1 }}
        action={
          <Button size="small" onClick={reset}>
            Reset
          </Button>
        }
      >
        {startError}
      </Alert>
    );
  }

  // Tracking state — show progress
  if (isTracking && progress) {
    return (
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">{progress.progress}</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.round(progress.progressPercent * 100)}
        />
      </Paper>
    );
  }

  // Tracking but no progress yet — show indeterminate bar
  if (isTracking && !progress) {
    return (
      <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">Starting...</Typography>
        </Box>
        <LinearProgress />
      </Paper>
    );
  }

  // Complete state
  if (isComplete && progress) {
    return (
      <Alert
        severity="success"
        sx={{ py: compact ? 0.5 : 1 }}
        action={
          <Button size="small" onClick={reset}>
            Reset
          </Button>
        }
      >
        {progress.progress}
      </Alert>
    );
  }

  // Complete without progress (e.g., 0 emails)
  if (isComplete) {
    return (
      <Alert
        severity="success"
        sx={{ py: compact ? 0.5 : 1 }}
        action={
          <Button size="small" onClick={reset}>
            Reset
          </Button>
        }
      >
        Workflow completed.
      </Alert>
    );
  }

  return null;
}
